import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, MapPin, Clock, Plane, Euro } from 'lucide-react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ShowcasePage() {
  const { cardId } = useParams();
  const [card, setCard] = useState(null);
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadCardAndPackages = async () => {
      try {
        // Load the card details including packages
        const response = await fetch(`${API_URL}/api/homepage-cards/${cardId}`);
        if (!response.ok) {
          throw new Error('Card not found');
        }
        const data = await response.json();
        setCard(data);
        setPackages(data.packages || []);
      } catch (err) {
        console.error('Error loading showcase:', err);
        setError('Nu am putut încărca pachetele.');
      } finally {
        setLoading(false);
      }
    };

    loadCardAndPackages();
  }, [cardId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <div className="pt-24 pb-16 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gold"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (error || !card) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <div className="pt-24 pb-16 max-w-7xl mx-auto px-4">
          <div className="text-center py-16">
            <h1 className="text-2xl font-bold text-navy mb-4">Oops!</h1>
            <p className="text-navy/60 mb-6">{error || 'Cardul nu a fost găsit.'}</p>
            <Link to="/" className="text-gold hover:underline">
              Înapoi la pagina principală
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      
      {/* Hero Section */}
      <div className="relative h-64 md:h-80">
        <img
          src={card.image_url}
          alt={card.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-4 pb-8 w-full">
            <Link 
              to="/" 
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Înapoi
            </Link>
            <h1 className="font-heading font-bold text-3xl md:text-4xl text-white">
              {card.title}
            </h1>
            {card.description && (
              <p className="text-white/70 mt-2 max-w-2xl">
                {card.description}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Packages Grid */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        {packages.length === 0 ? (
          <div className="text-center py-16">
            <p className="text-navy/60">Nu există pachete în această selecție.</p>
          </div>
        ) : (
          <>
            <p className="text-navy/60 mb-6">
              {packages.length} {packages.length === 1 ? 'pachet' : 'pachete'} disponibile
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {packages.map((pkg) => (
                <Link
                  key={pkg.id}
                  to={`/oferte/${pkg.destination_slug || 'destinatie'}/${pkg.slug}`}
                  className="group bg-white rounded-2xl overflow-hidden border border-navy/10 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
                  data-testid={`showcase-package-${pkg.id}`}
                >
                  {/* Image */}
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src={pkg.image_url}
                      alt={pkg.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      loading="lazy"
                    />
                    <div className="absolute top-3 right-3 bg-white/95 backdrop-blur-sm px-3 py-1 rounded-full">
                      <span className="text-gold font-bold">{pkg.price}</span>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-5">
                    <h3 className="font-heading font-semibold text-navy text-lg mb-2 line-clamp-2 group-hover:text-gold transition-colors">
                      {pkg.title}
                    </h3>
                    
                    <div className="flex flex-wrap gap-3 text-sm text-navy/60">
                      {pkg.country && (
                        <div className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          <span>{pkg.country}</span>
                        </div>
                      )}
                      {pkg.duration && (
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{pkg.duration}</span>
                        </div>
                      )}
                    </div>
                    
                    {pkg.operator && (
                      <div className="mt-3 pt-3 border-t border-navy/10">
                        <span className="text-xs text-navy/50">
                          {pkg.operator}
                        </span>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}
